import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle, XCircle, Eye, MapPin, Phone, Mail, Edit, Save } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/apiService';

const OrdersManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [editForm, setEditForm] = useState({
    customerName: '',
    customerEmail: '',
    mobileNumber: '',
    fulladdress: '',
    productType: '',
    speedRequired: '',
    status: ''
  });

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    } else {
      loadOrders();
    }
  }, [navigate]);

  const loadOrders = async () => {
    try {
      const data = await adminAPI.getAllOrders();
      setOrders(data);
      setFilteredOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    }
  };

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(o => o.status.toLowerCase() === statusFilter));
    }
  }, [statusFilter, orders]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsViewDialogOpen(true);
  };

  const handleAcceptOrder = async (order) => {
    try {
      await adminAPI.updateOrder(order._id || order.id, 'Accepted', 'Order accepted by admin');
      await loadOrders();
      toast.success(`Order accepted!`);
    } catch (error) {
      console.error('Error accepting order:', error);
      toast.error('Failed to accept order');
    }
  };

  const handleRejectOrder = async (order) => {
    try {
      await adminAPI.updateOrder(order._id || order.id, 'Rejected', 'Order rejected by admin');
      await loadOrders();
      toast.error(`Order rejected!`);
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast.error('Failed to reject order');
    }
  };

  const handleOpenUpdateDialog = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusNote('');
    setIsUpdateDialogOpen(true);
  };

  const handleOpenEditDialog = (order) => {
    setSelectedOrder(order);
    setEditForm({
      customerName: order.customerName || '',
      customerEmail: order.customerEmail || '',
      mobileNumber: order.mobileNumber || '',
      fulladdress: order.fulladdress || '',
      productType: order.productType || '',
      speedRequired: order.speedRequired || '',
      status: order.status || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      // You would need to create an admin endpoint to update full order details
      // For now, we'll update what we can through the existing endpoint
      await adminAPI.updateOrder(
        selectedOrder._id || selectedOrder.id,
        editForm.status,
        `Order details updated by admin`
      );
      
      // Ideally, you'd have a dedicated endpoint like:
      // await adminAPI.updateOrderDetails(orderId, editForm);
      
      await loadOrders();
      toast.success('Order updated successfully!');
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !statusNote) {
      toast.error('Please select status and add a note');
      return;
    }

    try {
      await adminAPI.updateOrder(selectedOrder._id || selectedOrder.id, newStatus, statusNote);
      await loadOrders();
      toast.success(`Order updated to ${newStatus}!`);
      setIsUpdateDialogOpen(false);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { className: 'bg-orange-100 text-orange-800 hover:bg-orange-100' },
      'Accepted': { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100' },
      'In Progress': { className: 'bg-purple-100 text-purple-800 hover:bg-purple-100' },
      'Completed': { className: 'bg-green-100 text-green-800 hover:bg-green-100' },
      'Rejected': { className: 'bg-red-100 text-red-800 hover:bg-red-100' }
    };
    const config = statusConfig[status] || statusConfig['Pending'];
    return <Badge className={config.className}>{status}</Badge>;
  };

  const pendingCount = orders.filter(o => o.status === 'Pending').length;
  const acceptedCount = orders.filter(o => o.status === 'Accepted').length;
  const inProgressCount = orders.filter(o => o.status === 'In Progress').length;
  const completedCount = orders.filter(o => o.status === 'Completed').length;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Orders Management</h1>
              <p className="text-gray-700 text-lg font-medium">View and manage all customer orders</p>
            </div>
            <Button 
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-3 border-orange-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Pending Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-600">{pendingCount}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-blue-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Accepted Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600">{acceptedCount}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-purple-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple-600">{inProgressCount}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-green-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{completedCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Label className="font-semibold">Filter by Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="in progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">Showing {filteredOrders?.length || 0} orders</span>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>All Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Speed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders && filteredOrders.length > 0 ? (
                    filteredOrders.map((order) => (
                    <TableRow key={order._id || order.id} className="hover:bg-gray-50">
                      <TableCell className="font-mono text-sm font-medium">{order._id || order.id}</TableCell>
                      <TableCell>
                        {new Date(order.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="font-medium">{order.referrerName}</TableCell>
                      <TableCell>{order.customerName}</TableCell>
                      <TableCell>{order.productType}</TableCell>
                      <TableCell>{order.speedRequired || '-'}</TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-400 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-400 text-orange-600 hover:bg-orange-50"
                            onClick={() => handleOpenEditDialog(order)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {order.status === 'Pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleAcceptOrder(order)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => handleRejectOrder(order)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {(order.status === 'Accepted' || order.status === 'In Progress') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-400 text-purple-600 hover:bg-purple-50"
                              onClick={() => handleOpenUpdateDialog(order)}
                            >
                              Update
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        {filteredOrders ? 'No orders found' : 'Loading orders...'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* View Order Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>Order Details</DialogTitle>
              <DialogDescription>Complete order information</DialogDescription>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-semibold">Order ID</Label>
                    <p className="font-mono text-sm">{selectedOrder._id || selectedOrder.id}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold">Status</Label>
                    <div>{getStatusBadge(selectedOrder.status)}</div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-bold text-lg mb-3">Referrer Information</h3>
                  <p><strong>Name:</strong> {selectedOrder.userName || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedOrder.userEmail || 'N/A'}</p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-bold text-lg mb-3">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                      <p className="flex items-center gap-2 mt-2">
                        <Mail className="h-4 w-4" />
                        {selectedOrder.customerEmail}
                      </p>
                      <p className="flex items-center gap-2 mt-2">
                        <Phone className="h-4 w-4" />
                        {selectedOrder.mobileNumber}
                      </p>
                    </div>
                    <div>
                      <p><strong>Address:</strong></p>
                      <p className="text-sm">{selectedOrder.fulladdress || selectedOrder.address || 'N/A'}</p>
                      {selectedOrder.latitude && (
                        <p className="flex items-center gap-2 mt-2 text-sm">
                          <MapPin className="h-4 w-4" />
                          {selectedOrder.latitude}, {selectedOrder.longitude}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-bold text-lg mb-3">Product Information</h3>
                  <p><strong>Product:</strong> {selectedOrder.productType}</p>
                  {selectedOrder.speedRequired && (
                    <p><strong>Speed:</strong> {selectedOrder.speedRequired}</p>
                  )}
                </div>

                <div className="border-t pt-4">
                  <p><strong>Created At:</strong> {new Date(selectedOrder.createdAt).toLocaleString('en-IN')}</p>
                  {selectedOrder.notes && (
                    <div className="mt-2">
                      <p><strong>Admin Notes:</strong></p>
                      <p className="text-sm bg-gray-50 p-2 rounded">{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Update Status Dialog */}
        <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Update Order Status</DialogTitle>
              <DialogDescription>Update status and add progress note</DialogDescription>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Status: {selectedOrder.status}</Label>
                </div>
                <div className="space-y-2">
                  <Label>New Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status Note/Update</Label>
                  <Textarea
                    placeholder="e.g., Installation team assigned, Setup completed, etc."
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-green-400 bg-green-600 hover:bg-green-700"
                    onClick={handleUpdateStatus}
                  >
                    Update Status
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsUpdateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Order Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-6 w-6 text-orange-600" />
                Edit Order Details
              </DialogTitle>
              <DialogDescription>
                Update order information and customer details
              </DialogDescription>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                {/* Order ID */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Order ID</p>
                  <p className="font-mono font-bold">{selectedOrder._id || selectedOrder.id}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Customer Name */}
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input
                      value={editForm.customerName}
                      onChange={(e) => handleEditFormChange('customerName', e.target.value)}
                      placeholder="Enter customer name"
                    />
                  </div>

                  {/* Customer Email */}
                  <div className="space-y-2">
                    <Label>Customer Email</Label>
                    <Input
                      type="email"
                      value={editForm.customerEmail}
                      onChange={(e) => handleEditFormChange('customerEmail', e.target.value)}
                      placeholder="customer@example.com"
                    />
                  </div>

                  {/* Mobile Number */}
                  <div className="space-y-2">
                    <Label>Mobile Number *</Label>
                    <Input
                      value={editForm.mobileNumber}
                      onChange={(e) => handleEditFormChange('mobileNumber', e.target.value)}
                      placeholder="Enter mobile number"
                    />
                  </div>

                  {/* Product Type */}
                  <div className="space-y-2">
                    <Label>Product Type *</Label>
                    <Select 
                      value={editForm.productType} 
                      onValueChange={(value) => handleEditFormChange('productType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Internet Leaseline">Internet Leaseline</SelectItem>
                        <SelectItem value="Lease Line Internet">Lease Line Internet</SelectItem>
                        <SelectItem value="Enterprise Broadband">Enterprise Broadband</SelectItem>
                        <SelectItem value="CCTV Security Camera">CCTV Security Camera</SelectItem>
                        <SelectItem value="Internet Networking">Internet Networking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Speed Required */}
                  <div className="space-y-2">
                    <Label>Speed Required</Label>
                    <Input
                      value={editForm.speedRequired}
                      onChange={(e) => handleEditFormChange('speedRequired', e.target.value)}
                      placeholder="e.g., 100 Mbps"
                    />
                  </div>

                  {/* Order Status */}
                  <div className="space-y-2">
                    <Label>Order Status *</Label>
                    <Select 
                      value={editForm.status} 
                      onValueChange={(value) => handleEditFormChange('status', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Accepted">Accepted</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Full Address */}
                <div className="space-y-2">
                  <Label>Full Address *</Label>
                  <Textarea
                    value={editForm.fulladdress}
                    onChange={(e) => handleEditFormChange('fulladdress', e.target.value)}
                    placeholder="Enter complete address"
                    rows={3}
                  />
                </div>

                {/* Referrer Info (Read-only) */}
                <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Referrer Information (Read-only)</p>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Name: </span>
                      <span className="font-semibold">{selectedOrder.referrerName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email: </span>
                      <span className="font-semibold">{selectedOrder.referrerEmail || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-orange-400 bg-orange-600 hover:bg-orange-700"
                    onClick={handleSaveEdit}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsEditDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OrdersManagement;
